import * as THREE from 'three';

import { CHURCH_CONFIG, QUALITY_PRESETS } from '../config.js';
import { loadGLTF, prepareModel } from '../utils/loaders.js';

const ARCADE_BAYS = 5;
const CURVE_SEGMENTS = 20;
const FACADE_OFFSET = 0.025;

function createTriangularPrismGeometry(width, rise, length) {
  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;
  const positions = new Float32Array([
    -halfWidth, 0, -halfLength,
    halfWidth, 0, -halfLength,
    0, rise, -halfLength,
    -halfWidth, 0, halfLength,
    halfWidth, 0, halfLength,
    0, rise, halfLength,
  ]);
  const indices = [
    0, 2, 1,
    3, 4, 5,
    0, 3, 5, 0, 5, 2,
    1, 2, 5, 1, 5, 4,
    0, 1, 4, 0, 4, 3,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function createArchedPanelGeometry(width, height) {
  const radius = width * 0.5;
  const springY = height - radius;
  const shape = new THREE.Shape();
  shape.moveTo(-radius, 0);
  shape.lineTo(radius, 0);
  shape.lineTo(radius, springY);
  shape.absarc(0, springY, radius, 0, Math.PI, false);
  shape.closePath();
  return new THREE.ShapeGeometry(shape, CURVE_SEGMENTS);
}

function setTransform(mesh, position, rotation) {
  if (position) mesh.position.set(...position);
  if (rotation) mesh.rotation.set(...rotation);
  mesh.updateMatrix();
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/**
 * Loads the final GLB when present and otherwise builds a recognizable,
 * low-draw-call Romanesque placeholder at true physical scale.
 */
export class Church {
  constructor({
    renderer,
    loadingManager = THREE.DefaultLoadingManager,
    quality = 'MEDIUM',
  } = {}) {
    this.renderer = renderer;
    this.loadingManager = loadingManager;
    this.quality = QUALITY_PRESETS[quality] ?? QUALITY_PRESETS.MEDIUM;
    this.group = new THREE.Group();
    this.group.name = 'church';
    this.content = null;
    this.animations = [];
    this.blockedMeshes = [];
    this.blockedBounds = new THREE.Box3();
    this.usedPlaceholder = false;
    this.loadError = null;
    this._loadingPromise = null;

    this._applyConfiguredTransform();
  }

  _applyConfiguredTransform() {
    this.group.position.fromArray(CHURCH_CONFIG.position);
    this.group.rotation.fromArray(CHURCH_CONFIG.rotation);

    if (Array.isArray(CHURCH_CONFIG.scale)) {
      this.group.scale.fromArray(CHURCH_CONFIG.scale);
    } else {
      this.group.scale.setScalar(CHURCH_CONFIG.scale);
    }
  }

  load() {
    if (!this._loadingPromise) {
      this._loadingPromise = this._loadModelOrPlaceholder();
    }
    return this._loadingPromise;
  }

  async _loadModelOrPlaceholder() {
    try {
      const gltf = await loadGLTF(CHURCH_CONFIG.modelUrl, {
        manager: this.loadingManager,
        dracoPath: CHURCH_CONFIG.dracoPath,
        ktx2Path: CHURCH_CONFIG.ktx2Path,
        renderer: this.renderer,
      });
      const model = gltf?.scene ?? gltf;

      if (!model?.isObject3D) {
        throw new TypeError('El archivo GLB no contiene una escena Three.js válida.');
      }

      model.name ||= 'iglesia-model';
      this.animations = gltf?.animations ?? [];
      this._configureLoadedModel(model);
      this._replaceContent(model, false);
      return {
        object: model,
        usedPlaceholder: false,
        error: null,
      };
    } catch (error) {
      this.loadError = error;
      console.error(
        'No se ha podido cargar el modelo de la iglesia. Se utilizará el placeholder.',
        error,
      );
      const placeholder = this._createPlaceholder();
      this._replaceContent(placeholder, true);
      return {
        object: placeholder,
        usedPlaceholder: true,
        error,
      };
    }
  }

  _configureLoadedModel(model) {
    prepareModel(model, {
      castShadow: Boolean(this.quality.shadows && CHURCH_CONFIG.castShadow),
      receiveShadow: CHURCH_CONFIG.receiveShadow,
    });

    model.traverse((object) => {
      if (!object.isMesh) return;
      object.userData.navigationBlocker = true;
    });
  }

  _replaceContent(nextContent, usedPlaceholder) {
    if (this.content) {
      this.group.remove(this.content);
      this._disposeObject(this.content);
    }

    this.content = nextContent;
    this.usedPlaceholder = usedPlaceholder;
    this.group.add(nextContent);
    this._refreshNavigationData();
  }

  _refreshNavigationData() {
    this.blockedMeshes.length = 0;
    this.content?.traverse((object) => {
      if (object.isMesh) {
        object.userData.navigationBlocker = true;
        this.blockedMeshes.push(object);
      }
    });

    this.group.updateWorldMatrix(true, true);
    this.blockedBounds.setFromObject(this.group, true);
  }

  _createPlaceholder() {
    const dimensions = CHURCH_CONFIG.placeholder;
    const root = new THREE.Group();
    root.name = 'church-placeholder';

    const materials = {
      stone: new THREE.MeshStandardMaterial({
        color: dimensions.stoneColor,
        roughness: 0.93,
        metalness: 0,
      }),
      stoneLight: new THREE.MeshStandardMaterial({
        color: dimensions.stoneAccentColor,
        roughness: 0.9,
        metalness: 0,
      }),
      roof: new THREE.MeshStandardMaterial({
        color: dimensions.roofColor,
        roughness: 0.88,
        metalness: 0,
      }),
      opening: new THREE.MeshStandardMaterial({
        color: dimensions.openingColor,
        roughness: 1,
        metalness: 0,
      }),
      wood: new THREE.MeshStandardMaterial({
        color: dimensions.openingColor,
        roughness: 0.96,
        metalness: 0,
      }),
    };

    const addMesh = (geometry, material, name, position, rotation) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = name;
      mesh.castShadow = Boolean(this.quality.shadows && CHURCH_CONFIG.castShadow);
      mesh.receiveShadow = CHURCH_CONFIG.receiveShadow;
      mesh.userData.navigationBlocker = true;
      setTransform(mesh, position, rotation);
      root.add(mesh);
      return mesh;
    };

    const {
      width,
      length,
      wallHeight,
      naveHeight,
      towerSize,
      towerHeight,
      apseRadius,
    } = dimensions;
    const roofRise = naveHeight - wallHeight;
    const roofWidth = width * 1.08;
    const towerZ = length * 0.5 - towerSize * 0.62;

    addMesh(
      new THREE.BoxGeometry(width, wallHeight, length),
      materials.stone,
      'placeholder-nave',
      [0, wallHeight * 0.5, 0],
    );
    addMesh(
      createTriangularPrismGeometry(roofWidth, roofRise, length * 1.04),
      materials.roof,
      'placeholder-nave-roof',
      [0, wallHeight, 0],
    );

    addMesh(
      new THREE.CylinderGeometry(
        apseRadius,
        apseRadius,
        wallHeight * 0.86,
        CURVE_SEGMENTS,
        1,
        false,
        Math.PI * 0.5,
        Math.PI,
      ),
      materials.stone,
      'placeholder-apse',
      [0, wallHeight * 0.43, -length * 0.5],
    );
    addMesh(
      new THREE.CylinderGeometry(
        0,
        apseRadius * 1.08,
        roofRise * 0.9,
        CURVE_SEGMENTS,
        1,
        false,
        Math.PI * 0.5,
        Math.PI,
      ),
      materials.roof,
      'placeholder-apse-roof',
      [0, wallHeight * 0.86 + roofRise * 0.45, -length * 0.5],
    );

    addMesh(
      new THREE.BoxGeometry(towerSize, towerHeight, towerSize),
      materials.stone,
      'placeholder-bell-tower',
      [0, towerHeight * 0.5, towerZ],
    );
    const towerRoofHeight = towerSize * 0.72;
    addMesh(
      new THREE.ConeGeometry(towerSize * 0.78, towerRoofHeight, 4),
      materials.roof,
      'placeholder-tower-roof',
      [0, towerHeight + towerRoofHeight * 0.5, towerZ],
      [0, Math.PI * 0.25, 0],
    );

    this._addTowerOpenings({
      root,
      addMesh,
      materials,
      towerSize,
      towerHeight,
      towerZ,
    });
    this._addRomanesquePortal({
      addMesh,
      materials,
      width,
      length,
      wallHeight,
    });
    this._addPortico({
      root,
      addMesh,
      materials,
      width,
      length,
      wallHeight,
    });
    this._addButtresses({ addMesh, materials, width, length, wallHeight });

    return root;
  }

  _addTowerOpenings({ root, addMesh, materials, towerSize, towerHeight, towerZ }) {
    const openingWidth = towerSize * 0.22;
    const openingHeight = towerSize * 0.48;
    const openingBase = towerHeight * 0.69;
    const openingGeometry = createArchedPanelGeometry(openingWidth, openingHeight);
    const lateralOffset = towerSize * 0.18;
    const frontZ = towerZ + towerSize * 0.5 + FACADE_OFFSET;

    addMesh(
      openingGeometry,
      materials.opening,
      'placeholder-bell-opening-left',
      [-lateralOffset, openingBase, frontZ],
    );
    addMesh(
      openingGeometry,
      materials.opening,
      'placeholder-bell-opening-right',
      [lateralOffset, openingBase, frontZ],
    );

    const sideOpening = new THREE.Mesh(openingGeometry, materials.opening);
    sideOpening.name = 'placeholder-bell-opening-side';
    sideOpening.position.set(
      towerSize * 0.5 + FACADE_OFFSET,
      openingBase,
      towerZ,
    );
    sideOpening.rotation.y = Math.PI * 0.5;
    sideOpening.castShadow = false;
    sideOpening.receiveShadow = CHURCH_CONFIG.receiveShadow;
    sideOpening.userData.navigationBlocker = true;
    sideOpening.updateMatrix();
    sideOpening.matrixAutoUpdate = false;
    root.add(sideOpening);
  }

  _addRomanesquePortal({ addMesh, materials, width, length, wallHeight }) {
    const portalWidth = width * 0.22;
    const portalHeight = Math.min(wallHeight * 0.62, portalWidth * 1.7);
    const portalRadius = portalWidth * 0.5;
    const facadeZ = length * 0.5 + FACADE_OFFSET;

    addMesh(
      createArchedPanelGeometry(portalWidth, portalHeight),
      materials.wood,
      'placeholder-romanesque-door',
      [0, 0.04, facadeZ],
    );

    const archGeometry = new THREE.TorusGeometry(
      portalRadius * 1.08,
      portalWidth * 0.075,
      6,
      CURVE_SEGMENTS,
      Math.PI,
    );
    addMesh(
      archGeometry,
      materials.stoneLight,
      'placeholder-portal-arch-inner',
      [0, portalHeight - portalRadius, facadeZ + FACADE_OFFSET],
    );
    const outerArch = addMesh(
      archGeometry,
      materials.stoneLight,
      'placeholder-portal-arch-outer',
      [0, portalHeight - portalRadius, facadeZ + FACADE_OFFSET * 1.6],
    );
    outerArch.scale.setScalar(1.2);
    outerArch.updateMatrix();

    const jambGeometry = new THREE.BoxGeometry(
      portalWidth * 0.12,
      portalHeight - portalRadius,
      portalWidth * 0.1,
    );
    const jambY = (portalHeight - portalRadius) * 0.5;
    addMesh(
      jambGeometry,
      materials.stoneLight,
      'placeholder-portal-jamb-left',
      [-portalRadius * 1.1, jambY, facadeZ],
    );
    addMesh(
      jambGeometry,
      materials.stoneLight,
      'placeholder-portal-jamb-right',
      [portalRadius * 1.1, jambY, facadeZ],
    );
  }

  _addPortico({ root, addMesh, materials, width, length, wallHeight }) {
    const porticoDepth = width * 0.24;
    const arcadeLength = length * 0.62;
    const bayWidth = arcadeLength / ARCADE_BAYS;
    const columnHeight = wallHeight * 0.52;
    const columnX = width * 0.5 + porticoDepth * 0.78;
    const columnGeometry = new THREE.CylinderGeometry(
      bayWidth * 0.065,
      bayWidth * 0.085,
      columnHeight,
      8,
    );
    const columns = new THREE.InstancedMesh(
      columnGeometry,
      materials.stoneLight,
      ARCADE_BAYS + 1,
    );
    columns.name = 'placeholder-portico-columns';
    columns.castShadow = Boolean(this.quality.shadows && CHURCH_CONFIG.castShadow);
    columns.receiveShadow = CHURCH_CONFIG.receiveShadow;
    columns.userData.navigationBlocker = true;

    const matrix = new THREE.Matrix4();
    for (let index = 0; index <= ARCADE_BAYS; index += 1) {
      const z = -arcadeLength * 0.5 + bayWidth * index;
      matrix.makeTranslation(columnX, columnHeight * 0.5, z);
      columns.setMatrixAt(index, matrix);
    }
    columns.instanceMatrix.needsUpdate = true;
    columns.computeBoundingSphere();
    root.add(columns);

    const archRadius = bayWidth * 0.42;
    const archGeometry = new THREE.TorusGeometry(
      archRadius,
      bayWidth * 0.055,
      6,
      16,
      Math.PI,
    );
    for (let index = 0; index < ARCADE_BAYS; index += 1) {
      const z = -arcadeLength * 0.5 + bayWidth * (index + 0.5);
      addMesh(
        archGeometry,
        materials.stoneLight,
        `placeholder-portico-arch-${index + 1}`,
        [columnX, columnHeight, z],
        [0, Math.PI * 0.5, 0],
      );
    }

    const roof = addMesh(
      new THREE.BoxGeometry(porticoDepth * 1.08, bayWidth * 0.14, arcadeLength + bayWidth * 0.3),
      materials.roof,
      'placeholder-portico-roof',
      [width * 0.5 + porticoDepth * 0.5, columnHeight + archRadius * 0.72, 0],
      [0, 0, -0.12],
    );
    roof.userData.navigationBlocker = true;
  }

  _addButtresses({ addMesh, materials, width, length, wallHeight }) {
    const buttressWidth = width * 0.07;
    const buttressDepth = width * 0.1;
    const buttressHeight = wallHeight * 0.72;
    const geometry = new THREE.BoxGeometry(
      buttressWidth,
      buttressHeight,
      buttressDepth,
    );
    const zPositions = [-length * 0.3, length * 0.12];

    zPositions.forEach((z, index) => {
      addMesh(
        geometry,
        materials.stoneLight,
        `placeholder-buttress-north-${index + 1}`,
        [-width * 0.5 - buttressWidth * 0.35, buttressHeight * 0.5, z],
      );
    });
  }

  containsHorizontalPoint(point, clearance = 0) {
    const bounds = this.blockedBounds;
    if (bounds.isEmpty()) return false;

    return (
      point.x >= bounds.min.x - clearance
      && point.x <= bounds.max.x + clearance
      && point.z >= bounds.min.z - clearance
      && point.z <= bounds.max.z + clearance
    );
  }

  getBlockedBounds(target = new THREE.Box3()) {
    return target.copy(this.blockedBounds);
  }

  _disposeObject(root) {
    const geometries = new Set();
    const materials = new Set();
    const textures = new Set();

    root.traverse((object) => {
      if (!object.isMesh) return;
      if (object.geometry) geometries.add(object.geometry);
      const objectMaterials = Array.isArray(object.material)
        ? object.material
        : [object.material];
      objectMaterials.forEach((material) => {
        if (!material) return;
        materials.add(material);
        Object.values(material).forEach((value) => {
          if (value?.isTexture) textures.add(value);
        });
      });
    });

    textures.forEach((texture) => texture.dispose());
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
  }

  dispose() {
    if (this.content) {
      this.group.remove(this.content);
      this._disposeObject(this.content);
      this.content = null;
    }
    this.group.removeFromParent();
    this.blockedMeshes.length = 0;
    this.blockedBounds.makeEmpty();
    this.animations.length = 0;
  }
}

export default Church;
